const chapter = {
  id: 'applications-of-derivatives',
  slug: 'applications-of-derivatives',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Applications of Derivatives',
        hindi: 'अवकलज के अनुप्रयोग',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '3–4 questions in JEE Main, 2 in Advanced',
        hindi: 'जेईई मेन में 3-4 प्रश्न, एडवांस्ड में 2',
  },
  syllabusTags: ['Calculus', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'rate',
      title: {
        english: 'Rate of Change',
        hindi: 'परिवर्तन की दर',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: '$\\dfrac{dy}{dx}$ represents the **instantaneous rate of change** of $y$ w.r.t. $x$. If two quantities depend on time $t$, related rates are obtained by differentiating the connecting equation w.r.t. $t$.',
        hindi: '$\\dfrac{dy}{dx}$ $y$ w.r.t. के **परिवर्तन की तात्कालिक दर** को दर्शाता है। $x$. यदि दो मात्राएँ समय $t$ पर निर्भर करती हैं, तो कनेक्टिंग समीकरण w.r.t. को अलग करके संबंधित दरें प्राप्त की जाती हैं। $t$.',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A balloon, which is spherical, has its radius increasing at $2$ cm/s. How fast is its volume increasing when $r = 5$ cm?',
        hindi: 'एक गुब्बारा, जो गोलाकार है, उसकी त्रिज्या $2$ cm/s पर बढ़ रही है। $r = 5$ सेमी होने पर इसका आयतन कितनी तेजी से बढ़ रहा है?',
          },
          solution: {
            english: '$V = \\tfrac{4}{3}\\pi r^3,\\ \\dfrac{dV}{dt} = 4\\pi r^2 \\dfrac{dr}{dt} = 4\\pi (25)(2) = 200\\pi$ cm³/s.',
        hindi: '$V = \\tfrac{4}{3}\\pi r^3,\\ \\dfrac{dV}{dt} = 4\\pi r^2 \\dfrac{dr}{dt} = 4\\pi (25)(2) = 200\\pi$ cm³/s.',
          },
        },
      ],
    },
    {
      id: 'tangent-normal',
      title: {
        english: 'Tangent and Normal',
        hindi: 'स्पर्श रेखा और अभिलम्ब',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Tangent and normal lines',
        hindi: 'स्पर्शरेखा और सामान्य रेखाएँ',
          },
          statement: {
            english: `At point $(x_0, y_0)$ on curve $y = f(x)$ with slope $m = f'(x_0)$:
• **Tangent**: $y - y_0 = m(x - x_0)$.
• **Normal**: $y - y_0 = -\\dfrac{1}{m}(x - x_0)$ (if $m \\ne 0$).`,
            hindi: 'ढलान $m = f\\\'(x_0)$ के साथ वक्र $y = f(x)$ पर बिंदु $(x_0, y_0)$ पर: • **स्पर्शरेखा**: $y - y_0 = m(x - x_0)$. • **सामान्य**: $y - y_0 = -\\dfrac{1}{m}(x - x_0)$ (यदि $m \\ne 0$)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Angle between two curves',
        hindi: 'दो वक्रों के बीच का कोण',
          },
          statement: {
            english: 'At a point of intersection, the angle $\\phi$ between curves with slopes $m_1, m_2$ satisfies $\\tan\\phi = \\left|\\dfrac{m_1 - m_2}{1 + m_1 m_2}\\right|$. Curves are **orthogonal** iff $m_1 m_2 = -1$.',
        hindi: 'प्रतिच्छेदन बिंदु पर, $m_1, m_2$ ढलान वाले वक्रों के बीच का कोण $m_1, m_2$ $\\tan\\phi = \\left|\\dfrac{m_1 - m_2}{1 + m_1 m_2}\\right|$ को संतुष्ट करता है। वक्र **ऑर्थोगोनल** हैं यदि $m_1 m_2 = -1$।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find equation of tangent and normal to $y = x^3 - 2x + 1$ at $x = 1$.',
        hindi: '$x = 1$ पर $y = x^3 - 2x + 1$ की स्पर्श रेखा और अभिलम्ब का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: `$y(1) = 0,\\ y'(x) = 3x^2 - 2,\\ y'(1) = 1$.
Tangent: $y = x - 1$. Normal: $y = -(x - 1) = -x + 1$.`,
            hindi: '$y(1) = 0,\\\\\\\\ y\\\'(x) = 3x^2 - 2,\\\\\\\\ y\\\'(1) = 1$. स्पर्शरेखा: $y = x - 1$. सामान्य: $y = -(x - 1) = -x + 1$.',
          },
        },
      ],
    },
    {
      id: 'monotonicity',
      title: {
        english: 'Monotonicity (Increasing/Decreasing)',
        hindi: 'एकदिष्टता (वर्धमान/ह्रासमान)',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'First derivative test for monotonicity',
        hindi: 'एकरसता के लिए पहला व्युत्पन्न परीक्षण',
          },
          statement: {
            english: `On an interval $I$:
• $f'(x) > 0$ on $I \\Rightarrow f$ is **strictly increasing** on $I$.
• $f'(x) < 0$ on $I \\Rightarrow f$ is **strictly decreasing** on $I$.
• $f'(x) \\ge 0$ on $I \\Rightarrow f$ is non-decreasing.`,
            hindi: 'अंतराल पर $I$: • $f\\\'(x) > 0$ को $I \\Rightarrow f$ पर $I$ को **सख्ती से बढ़ाया** जा रहा है। • $I \\Rightarrow f$ पर $f\\\'(x) < 0$ $I$ पर **सख्ती से घट रहा है**। • $f\\\'(x) \\ge 0$ पर $I \\Rightarrow f$ कम नहीं हो रहा है।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 220" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="130" x2="360" y2="130" stroke="#374151" stroke-width="1.2"/>
  <line x1="60" y1="20" x2="60" y2="200" stroke="#374151" stroke-width="1.2"/>
  <polyline points="60,197.5 63,187.6 66,178.4 69,170.0 72,162.3 75,155.3 78,149.0 81,143.4 84,138.3 87,133.9 90,130.0 93,126.7 96,123.8 99,121.5 102,119.6 105,118.2 108,117.1 111,116.5 114,116.2 117,116.2 120,116.5 123,117.1 126,117.9 129,118.9 132,120.2 135,121.6 138,123.1 141,124.7 144,126.4 147,128.2 150,130.0 153,131.8 156,133.6 159,135.3 162,136.9 165,138.4 168,139.8 171,141.1 174,142.1 177,142.9 180,143.5 183,143.8 186,143.8 189,143.5 192,142.9 195,141.8 198,140.4 201,138.5 204,136.2 207,133.3 210,130.0 213,126.1 216,121.7 219,116.6 222,111.0 225,104.7 228,97.7 231,90.0 234,81.6 237,72.4 240,62.5 243,51.7 246,40.1 249,27.7 252,14.3 255,0.1 258,-15.2 261,-31.3 264,-48.5 267,-66.7 270,-86.0 273,-106.3 276,-127.8 279,-150.4 282,-174.1 285,-199.1 288,-225.2 291,-252.6 294,-281.3 297,-311.2 300,-342.5 303,-375.1 306,-409.1 309,-444.5 312,-481.4 315,-519.7 318,-559.5 321,-600.8 324,-643.6 327,-688.0 330,-734.0 333,-781.6 336,-830.9 339,-881.8 342,-934.4 345,-988.8 348,-1044.9 351,-1102.8 354,-1162.5 357,-1224.1 360,-1287.5" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <circle cx="107.32050807568876" cy="134" r="3.5" fill="#dc2626"/>
  <text x="97.32050807568876" y="124" font-size="11" fill="#b91c1c">local max</text>
  <text x="240" y="195" font-size="11" fill="#047857">local min</text>
  <circle cx="270" cy="155" r="3.5" fill="#10b981"/>
  <text x="190" y="208" font-size="12" fill="#6b7280" text-anchor="middle">f'(x) = 0 at critical points; sign of f' tells increasing/decreasing</text>
</svg>`,
          caption: {
            english: 'Sign of f′ alternates between intervals: positive ⇒ rising, negative ⇒ falling.',
        hindi: 'f′ का चिह्न अंतरालों के बीच वैकल्पिक होता है: सकारात्मक ⇒ बढ़ना, नकारात्मक ⇒ गिरना।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find intervals of increase/decrease for $f(x) = x^3 - 6x^2 + 9x + 1$.',
        hindi: '$f(x) = x^3 - 6x^2 + 9x + 1$ के लिए वृद्धि/कमी के अंतराल ज्ञात कीजिए।',
          },
          solution: {
            english: `$f'(x) = 3x^2 - 12x + 9 = 3(x - 1)(x - 3)$.
• $f' > 0$ on $(-\\infty, 1) \\cup (3, \\infty)$ — increasing.
• $f' < 0$ on $(1, 3)$ — decreasing.`,
            hindi: '$f\\\'(x) = 3x^2 - 12x + 9 = 3(x - 1)(x - 3)$. • $f\\\' > 0$ को $(-\\infty, 1) \\cup (3, \\infty)$ पर — बढ़ रहा है। • $f\\\' < 0$ पर $(1, 3)$ — घट रहा है।',
          },
        },
      ],
    },
    {
      id: 'maxima-minima',
      title: {
        english: 'Maxima and Minima',
        hindi: 'उच्चिष्ठ और निम्निष्ठ',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Critical point',
        hindi: 'महत्वपूर्ण बिन्दू',
          },
          body: {
            english: '$c$ is a **critical point** of $f$ if $f\'(c) = 0$ or $f\'(c)$ does not exist. Local extrema can occur **only** at critical points.',
        hindi: 'यदि $f\\\'(c) = 0$ या $f\\\'(c)$ मौजूद नहीं है तो $c$ $f$ का **महत्वपूर्ण बिंदु** है। स्थानीय चरम सीमा **केवल** महत्वपूर्ण बिंदुओं पर हो सकती है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'First Derivative Test',
        hindi: 'पहला व्युत्पन्न परीक्षण',
          },
          statement: {
            english: `At a critical point $c$, if $f'$ changes from:
• $+$ to $-$: $f$ has a **local maximum** at $c$.
• $-$ to $+$: $f$ has a **local minimum** at $c$.
• no sign change: neither (point of inflection or stationary).`,
            hindi: 'एक महत्वपूर्ण बिंदु $c$ पर, यदि $f\\\'$ इससे बदलता है: • $+$ से $-$: $f$ का **स्थानीय अधिकतम** $c$ है। • $-$ से $+$: $f$ का **स्थानीय न्यूनतम** $c$ है। • कोई संकेत परिवर्तन नहीं: न ही (विभक्ति बिंदु या स्थिर)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Second Derivative Test',
        hindi: 'दूसरा व्युत्पन्न परीक्षण',
          },
          statement: {
            english: `If $f'(c) = 0$ and:
• $f''(c) < 0$ ⇒ local **maximum** at $c$.
• $f''(c) > 0$ ⇒ local **minimum** at $c$.
• $f''(c) = 0$ ⇒ test is **inconclusive** (use higher derivatives or first-derivative test).`,
            hindi: 'यदि $f\\\'(c) = 0$ और: • $f\\\'\\\'(c) < 0$ ⇒ स्थानीय **अधिकतम** $c$ पर। • $f\\\'\\\'(c) > 0$ ⇒ स्थानीय **न्यूनतम** $c$ पर। • $f\\\'\\\'(c) = 0$ ⇒ परीक्षण **अनिर्णायक** है (उच्च डेरिवेटिव या प्रथम-व्युत्पन्न परीक्षण का उपयोग करें)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Absolute extrema on a closed interval',
        hindi: 'एक बंद अंतराल पर पूर्ण चरम सीमा',
          },
          statement: {
            english: 'For continuous $f$ on $[a, b]$: compute $f$ at all critical points in $(a, b)$ and at endpoints $a, b$. The largest value is the absolute max; the smallest is the absolute min.',
        hindi: '$[a, b]$ पर निरंतर $f$ के लिए: $(a, b)$ में सभी महत्वपूर्ण बिंदुओं पर और अंतिम बिंदु $a, b$ पर $f$ की गणना करें। सबसे बड़ा मान पूर्ण अधिकतम है; सबसे छोटा पूर्ण न्यूनतम है.',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find absolute maxima and minima of $f(x) = x^3 - 3x + 2$ on $[-2, 2]$.',
        hindi: '$[-2, 2]$ पर $f(x) = x^3 - 3x + 2$ का पूर्ण अधिकतम और निम्नतम ज्ञात कीजिए।',
          },
          solution: {
            english: `$f'(x) = 3x^2 - 3 = 0 \\Rightarrow x = \\pm 1$.
Values: $f(-2) = 0,\\ f(-1) = 4,\\ f(1) = 0,\\ f(2) = 4$.
Absolute max $= 4$ at $x = -1, 2$; absolute min $= 0$ at $x = -2, 1$.`,
            hindi: '$f\\\'(x) = 3x^2 - 3 = 0 \\Rightarrow x = \\pm 1$. मान: $f(-2) = 0,\\\\\\\\ f(-1) = 4,\\\\\\\\ f(1) = 0,\\\\\\\\ f(2) = 4$. पूर्ण अधिकतम $= 4$ $x = -1, 2$ पर; $x = -2, 1$ पर पूर्ण न्यूनतम $= 0$।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'A rectangular box with square base and open top has volume $V$. Find dimensions minimizing surface area.',
        hindi: 'वर्गाकार आधार और खुले शीर्ष वाले एक आयताकार बॉक्स का आयतन $V$ है। सतह क्षेत्र को न्यूनतम करते हुए आयाम खोजें।',
          },
          solution: {
            english: `Let side $= x$, height $= h$. $V = x^2 h \\Rightarrow h = V/x^2$.
Surface $S = x^2 + 4xh = x^2 + 4V/x$.
$S'(x) = 2x - 4V/x^2 = 0 \\Rightarrow x^3 = 2V \\Rightarrow x = (2V)^{1/3}$.
$S''(x) = 2 + 8V/x^3 > 0$, so minimum.
Then $h = V/x^2 = x/2$.`,
            hindi: 'मान लीजिए भुजा $= x$, ऊंचाई $= h$। $V = x^2 h \\Rightarrow h = V/x^2$. सतह $S = x^2 + 4xh = x^2 + 4V/x$. $S\\\'(x) = 2x - 4V/x^2 = 0 \\Rightarrow x^3 = 2V \\Rightarrow x = (2V)^{1/3}$. $S\\\'\\\'(x) = 2 + 8V/x^3 > 0$, इतना न्यूनतम। फिर $h = V/x^2 = x/2$.',
          },
        },
      ],
    },
    {
      id: 'concavity',
      title: {
        english: 'Concavity & Inflection Points',
        hindi: 'अवतलता और नति परिवर्तन बिन्दु',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Concavity',
        hindi: 'अवतलता',
          },
          body: {
            english: '$f$ is **concave up** on $I$ if $f\'\'(x) > 0$ on $I$ (curve opens upward, like $\\smile$). **Concave down** if $f\'\'(x) < 0$ ($\\frown$). A **point of inflection** is where concavity changes (typically $f\'\'(c) = 0$ and signs differ on either side).',
        hindi: '$f$ $I$ पर **अवतल** है यदि $f\\\'\\\'(x) > 0$ $I$ पर है (वक्र ऊपर की ओर खुलता है, जैसे $\\smile$)। **अवतल नीचे** यदि $f\\\'\\\'(x) < 0$ ($\\frown$)। एक **विभक्ति बिंदु** वह है जहां अवतलता बदलती है (आमतौर पर $f\\\'\\\'(c) = 0$ और संकेत दोनों तरफ भिन्न होते हैं)।',
          },
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Find inflection points of $f(x) = x^4 - 6x^2$.',
        hindi: '$f(x) = x^4 - 6x^2$ के विभक्ति बिंदु खोजें।',
          },
          solution: {
            english: '$f\'\'(x) = 12x^2 - 12 = 12(x - 1)(x + 1)$. Sign changes at $x = \\pm 1$. Inflection points: $(\\pm 1, -5)$.',
        hindi: '$f\'\'(x) = 12x^2 - 12 = 12(x - 1)(x + 1)$. $x = \\pm 1$ पर परिवर्तन चिह्न। विभक्ति बिंदु: $(\\pm 1, -5)$।',
          },
        },
      ],
    },
    {
      id: 'mean-value',
      title: {
        english: 'Rolle\'s and Mean Value Theorems',
        hindi: 'रोले और माध्य मान प्रमेय',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Rolle\'s Theorem',
        hindi: 'रोले का प्रमेय',
          },
          statement: {
            english: 'If $f$ is continuous on $[a, b]$, differentiable on $(a, b)$, and $f(a) = f(b)$, then $\\exists c \\in (a, b)$ such that $f\'(c) = 0$.',
        hindi: 'यदि $f$ $[a, b]$ पर निरंतर है, $(a, b)$ और $f(a) = f(b)$ पर भिन्न है, तो $\\exists c \\in (a, b)$ इस प्रकार है कि $f\'(c) = 0$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Lagrange\'s Mean Value Theorem (LMVT)',
        hindi: 'लैग्रेंज का माध्य मान प्रमेय (LMVT)',
          },
          statement: {
            english: `If $f$ is continuous on $[a, b]$ and differentiable on $(a, b)$, then $\\exists c \\in (a, b)$ such that
$$f'(c) = \\dfrac{f(b) - f(a)}{b - a}.$$`,
            hindi: 'यदि $f$ $[a, b]$ पर सतत है और $(a, b)$ पर अवकलनीय है, तो $\\exists c \\in (a, b)$ इस प्रकार है कि $$f\\\'(c) = \\dfrac{f(b) - f(a)}{b - a}.$$',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Cauchy\'s MVT',
        hindi: 'कॉची का एमवीटी',
          },
          statement: {
            english: 'If $f, g$ continuous on $[a, b]$, differentiable on $(a, b)$, and $g\'(x) \\ne 0$, then $\\exists c$ such that $\\dfrac{f(b) - f(a)}{g(b) - g(a)} = \\dfrac{f\'(c)}{g\'(c)}$.',
        hindi: 'यदि $f, g$ $[a, b]$ पर निरंतर है, $(a, b)$ और $g\'(x) \\ne 0$ पर भिन्न है, तो $\\exists c$ इस प्रकार है कि $\\dfrac{f(b) - f(a)}{g(b) - g(a)} = \\dfrac{f\'(c)}{g\'(c)}$।',
          },
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Verify Rolle\'s theorem for $f(x) = x^2 - 4x + 3$ on $[1, 3]$.',
        hindi: '$[1, 3]$ पर $f(x) = x^2 - 4x + 3$ के लिए रोले के प्रमेय को सत्यापित करें।',
          },
          solution: {
            english: '$f$ continuous and differentiable; $f(1) = 0 = f(3)$. $f\'(x) = 2x - 4 = 0 \\Rightarrow c = 2 \\in (1, 3)$. ✓',
        hindi: '$f$ सतत और अवकलनीय; $f(1) = 0 = f(3)$. $f\'(x) = 2x - 4 = 0 \\Rightarrow c = 2 \\in (1, 3)$. ✓',
          },
        },
      ],
    },
    {
      id: 'inequalities',
      title: {
        english: 'Proving Inequalities via Derivatives',
        hindi: 'अवकलज द्वारा असमिकाएँ सिद्ध करना',
      },
      blocks: [
        {
          type: 'tip',
          text: {
            english: 'To prove $f(x) > g(x)$ on an interval, define $h(x) = f(x) - g(x)$, show $h$ has a useful endpoint value (e.g., $h(0) = 0$), then study sign of $h\'$ to conclude monotonicity.',
        hindi: 'किसी अंतराल पर $f(x) > g(x)$ को साबित करने के लिए, $h(x) = f(x) - g(x)$ को परिभाषित करें, दिखाएं कि $h$ का एक उपयोगी समापन बिंदु मान है (उदाहरण के लिए, $h(0) = 0$), फिर एकरसता का निष्कर्ष निकालने के लिए $h\\\'$ के संकेत का अध्ययन करें।',
          },
        },
        {
          type: 'example',
          number: 8,
          problem: {
            english: 'Prove $\\sin x < x$ for $x > 0$.',
        hindi: '$x > 0$ के लिए $\\sin x < x$ सिद्ध करें।',
          },
          solution: {
            english: 'Let $h(x) = x - \\sin x$. $h(0) = 0$. $h\'(x) = 1 - \\cos x \\ge 0$ with equality only at isolated points. So $h$ is strictly increasing on $(0, \\infty)$, hence $h(x) > 0$, i.e., $\\sin x < x$.',
        hindi: 'चलो $h(x) = x - \\sin x$। $h(0) = 0$. $h\\\'(x) = 1 - \\cos x \\ge 0$ केवल पृथक बिंदुओं पर समानता के साथ। तो $h$ $(0, \\infty)$ पर सख्ती से बढ़ रहा है, इसलिए $h(x) > 0$, यानी, $\\sin x < x$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$\\tan x > x$ for $x \\in (0, \\pi/2)$.',
        hindi: '$x \\in (0, \\pi/2)$ के लिए $\\tan x > x$।',
            },
            {
              english: '$e^x > 1 + x$ for all $x \\ne 0$.',
        hindi: '$e^x > 1 + x$ सभी $x \\ne 0$ के लिए।',
            },
            {
              english: '$\\ln(1 + x) < x$ for $x > 0$; $\\ln(1+x) > x - x^2/2$ for $x > 0$.',
        hindi: '$x > 0$ के लिए $\\ln(1 + x) < x$; $x > 0$ के लिए $\\ln(1+x) > x - x^2/2$।',
            },
            {
              english: '$\\dfrac{x}{1+x} < \\ln(1+x) < x$ for $x > 0$.',
        hindi: '$x > 0$ के लिए $\\dfrac{x}{1+x} < \\ln(1+x) < x$।',
            },
          ],
        },
      ],
    },
    {
      id: 'approximation',
      title: {
        english: 'Approximation & Errors',
        hindi: 'सन्निकटन और त्रुटियाँ',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Linear approximation',
        hindi: 'रैखिक सन्निकटन',
          },
          statement: {
            english: `For small $\\Delta x$:  $f(a + \\Delta x) \\approx f(a) + f'(a) \\Delta x$.
Differentials: $dy = f'(x) dx$.`,
            hindi: 'छोटे $\\Delta x$ के लिए: $f(a + \\Delta x) \\approx f(a) + f\\\'(a) \\Delta x$। विभेदक: $dy = f\\\'(x) dx$।',
          },
        },
        {
          type: 'example',
          number: 9,
          problem: {
            english: 'Approximate $\\sqrt{25.3}$.',
        hindi: 'अनुमानित $\\sqrt{25.3}$.',
          },
          solution: {
            english: 'Take $f(x) = \\sqrt x,\\ a = 25,\\ \\Delta x = 0.3$. $f\'(x) = 1/(2\\sqrt x),\\ f\'(25) = 0.1$. $\\sqrt{25.3} \\approx 5 + 0.1 \\times 0.3 = 5.03$.',
        hindi: '$f(x) = \\sqrt x,\\ a = 25,\\ \\Delta x = 0.3$ लें। $f\'(x) = 1/(2\\sqrt x),\\ f\'(25) = 0.1$. $\\sqrt{25.3} \\approx 5 + 0.1 \\times 0.3 = 5.03$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\tfrac{dy}{dt} = \\tfrac{dy}{dx} \\tfrac{dx}{dt}',
      desc: {
        english: 'Related rates (chain rule)',
        hindi: 'संबंधित दरें (श्रृंखला नियम)',
      },
    },
    {
      latex: 'y - y_0 = f\'(x_0)(x - x_0)',
      desc: {
        english: 'Tangent line',
        hindi: 'स्पर्श रेखा',
      },
    },
    {
      latex: 'f\'(x) > 0 \\Rightarrow \\text{increasing}',
      desc: {
        english: 'Monotonicity',
        hindi: 'दिष्टता',
      },
    },
    {
      latex: 'f\'(c) = 0,\\ f\'\'(c) < 0 \\Rightarrow \\text{local max}',
      desc: {
        english: 'Second derivative test',
        hindi: 'दूसरा व्युत्पन्न परीक्षण',
      },
    },
    {
      latex: 'f\'(c) = \\tfrac{f(b)-f(a)}{b-a}',
      desc: {
        english: 'LMVT',
        hindi: 'एलएमवीटी',
      },
    },
    {
      latex: 'f(a + \\Delta x) \\approx f(a) + f\'(a) \\Delta x',
      desc: {
        english: 'Linear approximation',
        hindi: 'रैखिक सन्निकटन',
      },
    },
    {
      latex: 'f\'\'(x) > 0 \\Leftrightarrow \\text{concave up}',
      desc: {
        english: 'Concavity',
        hindi: 'अवतलता',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Maxima',
      problem: {
        english: 'The maximum value of $f(x) = \\sin x + \\cos x$ on $[0, 2\\pi]$ is:',
        hindi: '$[0, 2\\pi]$ पर $f(x) = \\sin x + \\cos x$ का अधिकतम मान है:',
      },
      answer: {
        english: '$\\sqrt 2$',
        hindi: '$\\sqrt 2$',
      },
      solution: {
        english: '$f\'(x) = \\cos x - \\sin x = 0 \\Rightarrow \\tan x = 1 \\Rightarrow x = \\pi/4, 5\\pi/4$. $f(\\pi/4) = \\sqrt 2$ (max), $f(5\\pi/4) = -\\sqrt 2$ (min).',
        hindi: '$f\'(x) = \\cos x - \\sin x = 0 \\Rightarrow \\tan x = 1 \\Rightarrow x = \\pi/4, 5\\pi/4$. $f(\\pi/4) = \\sqrt 2$ (अधिकतम), $f(5\\pi/4) = -\\sqrt 2$ (न्यूनतम).',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Tangent',
      problem: {
        english: 'The tangent to curve $y = x^3 - x$ at point $(1, 0)$ also passes through:',
        hindi: 'बिंदु $(1, 0)$ पर वक्र $y = x^3 - x$ की स्पर्श रेखा भी गुजरती है:',
      },
      answer: {
        english: 'Compute slope $= 2$. Tangent: $y = 2(x - 1)$, i.e., $y = 2x - 2$.',
        hindi: 'ढलान की गणना करें $= 2$। स्पर्शरेखा: $y = 2(x - 1)$, यानी, $y = 2x - 2$।',
      },
      solution: {
        english: 'Slope $= 3x^2 - 1 = 2$ at $x = 1$. Line $y = 2x - 2$ passes through e.g. $(0, -2),\\ (2, 2)$.',
        hindi: 'ढलान $= 3x^2 - 1 = 2$ $x = 1$ पर। रेखा $y = 2x - 2$ उदाहरण के लिए गुजरती है। $(0, -2),\\ (2, 2)$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'LMVT',
      problem: {
        english: 'For $f(x) = x^2$ on $[1, 3]$, find $c$ as in LMVT.',
        hindi: '$[1, 3]$ पर $f(x) = x^2$ के लिए, LMVT की तरह $c$ खोजें।',
      },
      answer: {
        english: '$c = 2$',
        hindi: '$c = 2$',
      },
      solution: {
        english: 'LMVT: $f\'(c) = 2c = \\dfrac{9 - 1}{3 - 1} = 4 \\Rightarrow c = 2$.',
        hindi: 'एलएमवीटी: $f\'(c) = 2c = \\dfrac{9 - 1}{3 - 1} = 4 \\Rightarrow c = 2$।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Monotonicity',
      problem: {
        english: '$f(x) = x e^{x(1 - x)}$ is increasing on:',
        hindi: '$f(x) = x e^{x(1 - x)}$ बढ़ रहा है:',
      },
      answer: {
        english: '$[-1/2, 1]$',
        hindi: '$[-1/2, 1]$',
      },
      solution: {
        english: '$f\'(x) = e^{x(1-x)}(1 + x(1 - 2x))= e^{...}(-2x^2 + x + 1)$. Roots of quadratic: $x = 1, -1/2$. Sign $\\ge 0$ on $[-1/2, 1]$.',
        hindi: '$f\'(x) = e^{x(1-x)}(1 + x(1 - 2x))= e^{...}(-2x^2 + x + 1)$. द्विघात की जड़ें: $x = 1, -1/2$। $[-1/2, 1]$ पर $\\ge 0$ का चिह्न लगाएं।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Optimization',
      problem: {
        english: 'Find minimum value of $|x - 1| + |x - 2| + |x - 3|$ over $\\mathbb R$.',
        hindi: '$\\mathbb R$ पर $|x - 1| + |x - 2| + |x - 3|$ का न्यूनतम मान ज्ञात कीजिए।',
      },
      answer: {
        english: '$2$, attained at $x = 2$',
        hindi: '$2$, $x = 2$ पर प्राप्त हुआ',
      },
      solution: {
        english: 'Sum of distances minimized at the **median** of the points. Median $= 2$. Value $= 1 + 0 + 1 = 2$.',
        hindi: 'बिंदुओं के **मध्य** पर न्यूनतम की गई दूरियों का योग। माध्यिका $= 2$. मान $= 1 + 0 + 1 = 2$.',
      },
    },
  ],
  next: {
    slug: 'indefinite-integration',
    title: 'Indefinite Integration',
  },
};

export default chapter;
